import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Share, Plus, X } from 'lucide-react';
import { usePwaInstallPrompt } from '../hooks/useInstallPrompt';

/**
 * "Install app" hint shown on the first-screen of the PWA build to mobile
 * visitors who haven't already installed the app. Two flavours:
 *
 *   Android  → native `beforeinstallprompt` flow with an "Install" button
 *              that triggers the browser's own UI.
 *   iOS      → instructions modal with Share + "Add to Home Screen" icons,
 *              since iOS Safari does not expose a programmatic prompt.
 *
 * Suppressed entirely when:
 *   - build target is not PWA
 *   - platform is desktop
 *   - app is already running in standalone mode
 *   - user dismissed within the last 14 days (localStorage)
 */
export default function PwaInstallHint({ delayMs = 1400 }) {
  const [visible, setVisible] = useState(false);
  const { canPrompt, platform, isNative, promptInstall, dismiss } = usePwaInstallPrompt();

  useEffect(() => {
    if (process.env.REACT_APP_BUILD_TARGET !== 'pwa') return undefined;
    if (!canPrompt || !isNative) return undefined;
    const t = setTimeout(() => setVisible(true), delayMs);
    return () => clearTimeout(t);
  }, [canPrompt, isNative, delayMs]);

  if (process.env.REACT_APP_BUILD_TARGET !== 'pwa') return null;
  if (!canPrompt || !isNative || !visible) return null;

  const handleInstall = async () => {
    if (platform === 'android') {
      const ok = await promptInstall();
      if (ok) setVisible(false);
    }
  };

  const handleSkip = () => {
    dismiss();
    setVisible(false);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-6 pointer-events-none"
      >
        <div
          className="pointer-events-auto w-full max-w-sm rounded-2xl border border-white/10 backdrop-blur-md"
          style={{ background: 'rgba(24,24,27,0.92)' }}
        >
          <div className="p-4 flex items-start gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.14em] text-violet-400 font-semibold mb-1">
                Install app
              </div>
              {platform === 'ios' ? (
                <div className="text-xs text-white/80 leading-relaxed">
                  Tap <Share size={13} className="inline -mt-0.5 text-violet-400" /> and choose{' '}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/10 text-white">
                    <Plus size={11} /> Add to Home Screen
                  </span>{' '}
                  for offline play.
                </div>
              ) : (
                <div className="text-xs text-white/80 leading-relaxed">
                  Install for offline play without the browser bar.
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleSkip}
              aria-label="Dismiss"
              className="shrink-0 p-1 text-white/40 hover:text-white/70 transition-colors"
            >
              <X size={16} strokeWidth={2} />
            </button>
          </div>
          {platform === 'android' && (
            <div className="px-4 pb-4 flex gap-2">
              <button
                type="button"
                onClick={handleInstall}
                className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-xs font-semibold py-2 transition-colors"
              >
                Install
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="px-3 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 text-xs font-medium py-2 transition-colors"
              >
                Skip
              </button>
            </div>
          )}
          {platform === 'ios' && (
            <div className="pb-2 flex justify-center">
              <motion.div
                initial={{ y: 0, opacity: 0.4 }}
                animate={{ y: 6, opacity: 1 }}
                transition={{ repeat: Infinity, repeatType: 'reverse', duration: 0.9 }}
                className="text-violet-400 text-xl"
                aria-hidden
              >
                ↓
              </motion.div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
